import React, { useContext, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import { GlobalContext } from 'providers/GlobalProvider';
import { selectedTeamId } from '../common/selectedTeamId';
import BulkVerificationWizard, {
  BulkVerificationWizardDatabase,
} from './BulkVerificationWizard';
import {
  BULK_VERIFICATION_TILE_COPY,
  bulkVerificationTileState,
} from './bulkVerificationTile';
import { formatCount } from './bulkVerificationFormat';
import { BulkVerificationRunTracker } from './useBulkVerificationRun';

interface BulkVerificationActionProps {
  database: BulkVerificationWizardDatabase & {
    entity_add_on_enabled?: boolean;
  };
  /** The whole database, from its stats. */
  entityCount?: number;
  isScreening: boolean;
  /** List filters on the page, which a whole-database run ignores. */
  filtersActive: boolean;
  /** The page's Bulk Verification Run, from `useBulkVerificationRun`. */
  tracker: BulkVerificationRunTracker;
  /** Database Entity ids of the rows selected on the page. */
  selectedEntityIds?: string[];
}

const tooltipSx = {
  display: 'flex',
  padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--Background-200, var(--bg-colors))',
  color: 'var(--text-primary-color)',
  boxShadow: 'var(--es-card-shadow)',
  maxWidth: '280px',
  '& .MuiTooltip-arrow': {
    color: 'var(--Background-200, var(--bg-colors))',
  },
};

/** The button beside Run Screening that opens the Bulk Verification Run wizard. */
const BulkVerificationAction: React.FC<BulkVerificationActionProps> = ({
  database,
  entityCount,
  isScreening,
  filtersActive,
  tracker,
  selectedEntityIds,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [wizard, setWizard] = useState<{ entityIds?: string[] } | null>(null);

  const state = bulkVerificationTileState({
    hasEntityAddon: Boolean(cur_user?.hasEntityAddon),
    verificationDisabled: tracker.verificationDisabled,
    runInProgress: tracker.isRunning,
    databaseVerificationOff: database?.entity_add_on_enabled === false,
    isScreening: isScreening,
    entityCount: entityCount,
  });

  const { retryEntityIds, clearRetry } = tracker;
  useEffect(() => {
    if (!retryEntityIds) return;
    clearRetry();
    if (state === 'enabled') {
      setWizard({ entityIds: retryEntityIds });
    } else if (state !== 'hidden') {
      const why = BULK_VERIFICATION_TILE_COPY[state];
      enqueueSnackbar(`${why.title}. ${why.body}`, { variant: 'info' });
    }
  }, [retryEntityIds, clearRetry, state]);

  if (state === 'hidden') return null;

  const copy = BULK_VERIFICATION_TILE_COPY[state];
  const enabled = state === 'enabled';
  const selectedCount = selectedEntityIds?.length ?? 0;
  const label =
    selectedCount > 0
      ? `Verify ${formatCount(selectedCount)} selected`
      : 'Verify businesses';

  return (
    <>
      <Tooltip
        title={
          <span className='bdb-bulk-verify-tip'>
            <strong>{copy.title}</strong>
            <span>{copy.body}</span>
          </span>
        }
        placement='bottom-start'
        arrow
        describeChild
        componentsProps={{ tooltip: { sx: tooltipSx } }}
      >
        <button
          type='button'
          className='btn-outline bdb-verify-btn'
          aria-disabled={!enabled}
          onClick={() => {
            if (enabled) setWizard({ entityIds: selectedEntityIds });
          }}
        >
          <VerifiedUserOutlinedIcon
            className='bdb-verify-btn-icon'
            aria-hidden='true'
          />
          {label}
        </button>
      </Tooltip>
      {wizard && (
        <BulkVerificationWizard
          database={database}
          teamId={selectedTeamId(cur_user)}
          entityCount={entityCount}
          filtersActive={filtersActive}
          initialEntityIds={wizard.entityIds}
          onClose={() => setWizard(null)}
          onRunStarted={(run) => tracker.follow(run.runId)}
          onRunInProgress={() => {
            setWizard(null);
            tracker.followActive();
            enqueueSnackbar(
              'A verification run is already in progress for this database.',
              { variant: 'info' },
            );
          }}
        />
      )}
    </>
  );
};

export default BulkVerificationAction;
